import { useMemo, useState } from "react";
import Select from "react-select";
import { Button, Offcanvas } from "react-bootstrap";
import ReactSwitch from "react-switch";
import {
  DEFAULT_FX_TRADE_TYPE,
  FX_ASSET_TYPE,
  FX_TRADE_TYPE_OPTIONS,
  getAssetTypeLabel,
  getAssetTypeOptions,
  getCategoryOptionsForAsset,
  getCountryOptionsForAsset,
  getTickerOptionsForSelection,
} from "./TickerUniverseOptions";
import {
  autoScrollSelectComponents,
} from "../Common/SelectMenuScroll";

const getTheme = (isDark) =>
  isDark
    ? {
        pageBg: "#0B0F1A",
        surface: "#12172A",
        surface2: "#0F1424",
        border: "#232B4A",
        text: "#E8ECF8",
        muted: "#A6B0CF",
        primary: "#4C7DFF",
        primarySoft: "rgba(76,125,255,0.14)",
        btnOutlineBorder: "#2B3560",
        selectedBg: "#4C7DFF",
        selectedText: "#FFFFFF",
        menuShadow: "0 18px 40px rgba(0,0,0,0.45)",
      }
    : {
        pageBg: "#FFFFFF",
        surface: "#FFFFFF",
        surface2: "#FFFFFF",
        border: "#E9ECEF",
        text: "#0B1220",
        muted: "#5B6472",
        primary: "#0D6EFD",
        primarySoft: "rgba(13,110,253,0.10)",
        btnOutlineBorder: "#0D6EFD",
        selectedBg: "#0D6EFD",
        selectedText: "#FFFFFF",
        menuShadow: "0 14px 30px rgba(11,18,32,0.12)",
      };

const CoTraderCombinedMobileFiltersBar = ({
  universeOptions = {},
  selection,
  onSelectionChange,
  isDark,
  isDisabled = false,
  tradeTypeOptions = FX_TRADE_TYPE_OPTIONS,
  selectedLabel = "",
  showHeaderSummary = true,
  showSuggestions,
  setShowSuggestions,
  language,
  setLanguage,
  languageList = [],
  setLanguageKey,
}) => {
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const theme = useMemo(() => getTheme(isDark), [isDark]);
  const assetTypeOptions = useMemo(
    () => getAssetTypeOptions(universeOptions),
    [universeOptions],
  );
  const countryOptions = useMemo(
    () => getCountryOptionsForAsset(universeOptions, selection.assetType),
    [selection.assetType, universeOptions],
  );
  const categoryOptions = useMemo(
    () =>
      getCategoryOptionsForAsset(
        universeOptions,
        selection.assetType,
        selection.country,
      ),
    [selection.assetType, selection.country, universeOptions],
  );
  const tickerOptions = useMemo(
    () =>
      getTickerOptionsForSelection(
        universeOptions,
        selection.assetType,
        selection.country,
        selection.category,
      ),
    [selection.assetType, selection.category, selection.country, universeOptions],
  );
  const selectedTickerOption = useMemo(
    () =>
      tickerOptions.find((option) => option.value === selection.value) || null,
    [selection.value, tickerOptions],
  );
  const selectedAssetTypeOption = useMemo(
    () =>
      assetTypeOptions.find((option) => option.value === selection.assetType) ||
      null,
    [assetTypeOptions, selection.assetType],
  );
  const selectedCountryOption = useMemo(
    () =>
      countryOptions.find((option) => option.value === selection.country) || null,
    [countryOptions, selection.country],
  );
  const selectedCategoryOption = useMemo(
    () =>
      categoryOptions.find((option) => option.value === selection.category) || null,
    [categoryOptions, selection.category],
  );
  const selectedTradeTypeOption = useMemo(
    () =>
      tradeTypeOptions.find((option) => option.value === selection.tradeType) ||
      tradeTypeOptions[0] ||
      null,
    [selection.tradeType, tradeTypeOptions],
  );
  const isFxAsset = selection.assetType === FX_ASSET_TYPE;
  const languageOptions = useMemo(
    () => languageList.map((lang) => ({ value: lang, label: lang })),
    [languageList],
  );

  const selectPortalStyles = useMemo(
    () => ({
      menuPortal: (base) => ({ ...base, zIndex: 99999 }),
      container: (base) => ({ ...base, color: theme.text }),
      control: (base, state) => ({
        ...base,
        minHeight: "46px",
        borderRadius: "12px",
        backgroundColor: theme.surface2,
        borderColor: state.isFocused ? theme.primary : theme.border,
        boxShadow: state.isFocused ? `0 0 0 3px ${theme.primarySoft}` : "none",
        ":hover": {
          borderColor: state.isFocused ? theme.primary : theme.border,
        },
      }),
      valueContainer: (base) => ({ ...base, color: theme.text }),
      singleValue: (base) => ({ ...base, color: theme.text }),
      placeholder: (base) => ({ ...base, color: theme.muted }),
      input: (base) => ({ ...base, color: theme.text }),
      menu: (base) => ({
        ...base,
        zIndex: 99999,
        backgroundColor: theme.surface,
        border: `1px solid ${theme.border}`,
        borderRadius: 14,
        overflow: "hidden",
        boxShadow: theme.menuShadow,
      }),
      menuList: (base) => ({
        ...base,
        backgroundColor: theme.surface,
        padding: 6,
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
          ? theme.selectedBg
          : state.isFocused
            ? isDark
              ? "rgba(35,43,74,0.65)"
              : "rgba(13,110,253,0.08)"
            : theme.surface,
        color: state.isSelected ? theme.selectedText : theme.text,
        borderRadius: 10,
        cursor: "pointer",
        ":active": {
          backgroundColor: state.isSelected ? theme.selectedBg : theme.primarySoft,
        },
      }),
      indicatorSeparator: (base) => ({ ...base, backgroundColor: theme.border }),
      dropdownIndicator: (base) => ({ ...base, color: theme.muted }),
      noOptionsMessage: (base) => ({ ...base, color: theme.muted }),
    }),
    [isDark, theme],
  );

  return (
    <>
      <div
        className="px-3 pt-2 pb-2"
        style={{
          borderBottom: `1px solid ${theme.border}`,
          background: theme.surface,
        }}
      >
        <div
          className="d-flex align-items-center justify-content-between"
          style={{ justifyContent: showHeaderSummary ? "space-between" : "flex-end" }}
        >
          {showHeaderSummary ? (
            <div>
              <div
                className="fw-semibold"
                style={{ fontSize: "1.05rem", color: theme.text }}
              >
                {selectedLabel || "CoTrader"}
              </div>
              <div style={{ fontSize: "0.85rem", color: theme.muted }}>
                {getAssetTypeLabel(selection.assetType)}
              </div>
            </div>
          ) : null}

          <Button
            className="px-3 rounded-pill"
            onClick={() => setShowFilterMenu(true)}
            disabled={isDisabled}
            style={{
              background: "transparent",
              border: `1px solid ${theme.btnOutlineBorder}`,
              color: theme.primary,
            }}
          >
            <i className="fa-solid fa-sliders me-2" />
            Filters
          </Button>
        </div>
      </div>

      <Offcanvas
        show={showFilterMenu}
        onHide={() => setShowFilterMenu(false)}
        placement="bottom"
        style={{
          height: "85%",
          background: theme.pageBg,
          borderTop: `1px solid ${theme.border}`,
          paddingBottom: "60px",
        }}
      >
        <Offcanvas.Header
          closeButton
          style={{
            background: theme.surface,
            borderBottom: `1px solid ${theme.border}`,
          }}
        >
          <Offcanvas.Title style={{ color: theme.text }}>
            Filters & Settings
          </Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body>
          <div className="d-flex flex-column gap-3">
            <div>
              <div className="mb-2" style={{ color: theme.muted, fontSize: "0.85rem" }}>
                Asset Type
              </div>
              <Select
                options={assetTypeOptions}
                value={selectedAssetTypeOption}
                onChange={(nextAssetType) =>
                  onSelectionChange({
                    assetType: nextAssetType?.value || "",
                    country: null,
                    category: null,
                    tradeType: DEFAULT_FX_TRADE_TYPE,
                    value: "",
                  })
                }
                isDisabled={isDisabled || assetTypeOptions.length === 0}
                menuPortalTarget={document.body}
                styles={selectPortalStyles}
                components={autoScrollSelectComponents}
              />
            </div>

            {!isFxAsset && (
              <div>
                <div className="mb-2" style={{ color: theme.muted, fontSize: "0.85rem" }}>
                  Country
                </div>
                <Select
                  options={countryOptions}
                  value={selectedCountryOption}
                  onChange={(nextCountry) =>
                    onSelectionChange({
                      ...selection,
                      country: nextCountry?.value || null,
                      category: null,
                      value: "",
                    })
                  }
                  isDisabled={
                  isDisabled || !selection.assetType || countryOptions.length === 0
                  }
                  menuPortalTarget={document.body}
                  styles={selectPortalStyles}
                  components={autoScrollSelectComponents}
                />
              </div>
            )}

            {!isFxAsset && (
              <div>
                <div className="mb-2" style={{ color: theme.muted, fontSize: "0.85rem" }}>
                  Instrument / Category
                </div>
                <Select
                  options={categoryOptions}
                  value={selectedCategoryOption}
                  onChange={(nextCategory) =>
                    onSelectionChange({
                      ...selection,
                      category: nextCategory?.value || null,
                      value: "",
                    })
                  }
                  isDisabled={
                    isDisabled ||
                    !selection.assetType ||
                    !selection.country ||
                    categoryOptions.length === 0
                  }
                  menuPortalTarget={document.body}
                  styles={selectPortalStyles}
                  components={autoScrollSelectComponents}
                />
              </div>
            )}

            {isFxAsset && (
              <div>
                <div className="mb-2" style={{ color: theme.muted, fontSize: "0.85rem" }}>
                  Spot / Forward
                </div>
                <Select
                  options={tradeTypeOptions}
                  value={selectedTradeTypeOption}
                  onChange={(nextTradeType) =>
                    onSelectionChange({
                      ...selection,
                      tradeType: nextTradeType?.value || DEFAULT_FX_TRADE_TYPE,
                    })
                  }
                  isDisabled={isDisabled || tradeTypeOptions.length <= 1}
                  menuPortalTarget={document.body}
                  styles={selectPortalStyles}
                  components={autoScrollSelectComponents}
                />
              </div>
            )}

            <div>
              <div className="mb-2" style={{ color: theme.muted, fontSize: "0.85rem" }}>
                Ticker
              </div>
              <Select
                options={tickerOptions}
                value={selectedTickerOption}
                onChange={(nextTicker) =>
                  onSelectionChange({
                    ...selection,
                    value: nextTicker?.value || "",
                  })
                }
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.value}
                isDisabled={isDisabled || tickerOptions.length === 0}
                menuPortalTarget={document.body}
                styles={selectPortalStyles}
                components={autoScrollSelectComponents}
              />
            </div>

            <div
              className="d-flex align-items-center justify-content-between rounded-4 px-3 py-3"
              style={{
                background: theme.surface,
                border: `1px solid ${theme.border}`,
              }}
            >
              <div>
                <div style={{ color: theme.text, fontWeight: 600 }}>
                  Prompt Suggestions
                </div>
                <div style={{ color: theme.muted, fontSize: "0.85rem" }}>
                  Show suggested questions in GPT.
                </div>
              </div>
              <ReactSwitch
                checked={showSuggestions}
                onChange={setShowSuggestions}
                onColor={theme.primary}
                uncheckedIcon={false}
                checkedIcon={false}
              />
            </div>

            <div>
              <div className="mb-2" style={{ color: theme.muted, fontSize: "0.85rem" }}>
                Language
              </div>
              <Select
                options={languageOptions}
                value={languageOptions.find((option) => option.value === language) || null}
                onChange={(nextLanguage) => {
                  const value = nextLanguage?.value || "English";
                  setLanguage(value);
                  localStorage.setItem("user_language", value);
                  setLanguageKey((previousValue) => previousValue + 1);
                }}
                menuPortalTarget={document.body}
                styles={selectPortalStyles}
                components={autoScrollSelectComponents}
              />
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default CoTraderCombinedMobileFiltersBar;
